import { createSlice } from "@reduxjs/toolkit";
// import { subDays } from "date-fns";
import { format as formatTZ, toDate } from "date-fns-tz";
import { jsPDF as JsPDF } from "jspdf";
import "jspdf-autotable";

import {
  executeGetDiagnosticChartDataDaily,
  executeGetReferenceDiagnosticChartDataDaily,
  executeListJudgedDates,
  executeUpdateDiagnosticChartDataDaily,
} from "../api/ReportApi";
import { storeKeyName } from "../common/AppConstant";
import { handleError } from "../common/ErrorUtil";
import { ipaexg } from "../font/ipaexg00401/ipaexg-normal";
import corporate from "../image/corporate.jpg";
import {
  createGroupNumberString,
  getJudgeInfo,
  getOtherReportData,
  getOtherReportKindName,
  getOtherReportPartsName,
  getReportCategory,
  judgeLabel,
  judgeList,
  languageType,
  otherReportInfo,
  otherReportKindsList,
  otherReportList,
  pressType,
  reportData,
  reportFileLabel,
  reportFormLabel,
  reportLabel,
  reportOtherLabel,
  reportTableColor,
  targetData,
  trReferenceTableLabel,
} from "../model/Report";

// オブジェクトキー存在確認処理
const hasOwnProperty = (obj, key) =>
  Object.prototype.hasOwnProperty.call(obj, key);

// データ結合処理
const mergeData = (target, source) => {
  const isObject = (obj) =>
    obj && typeof obj === "object" && !Array.isArray(obj);
  const result = { ...target };

  if (isObject(target) && isObject(source)) {
    const sourceEntry = Object.entries(source);

    sourceEntry.forEach((entry) => {
      const sourceKey = entry[0];
      const sourceValue = entry[1];
      const targetValue = target[sourceKey];

      if (Array.isArray(sourceValue) && Array.isArray(targetValue)) {
        // 対象Keyが配列の場合は結合
        result[sourceKey] = targetValue.concat(...sourceValue);
      } else if (isObject(sourceValue) && hasOwnProperty(target, sourceKey)) {
        // 対象Keyがオブジェクトの場合は再帰的に処理
        result[sourceKey] = mergeData(targetValue, sourceValue);
      } else {
        // 対象Keyが結合先に存在しない場合 or 単体Keyの場合は結合先に追加
        Object.assign(result, { [sourceKey]: sourceValue });
      }
    });
  }
  return result;
};

// データ初期化処理
const resetData = () => mergeData({}, reportData);

// 対象外データクリア処理
const unusedData = (data, type) => {
  const result = JSON.parse(JSON.stringify(data));
  // データが正常に取得出来ている場合のみ実施
  if (result.office !== "") {
    switch (type) {
      case pressType.TR:
        // TR機種の場合、以下の値は対象外(空欄)とする
        // TR軸警告数(温度異常) - 前フィード
        result.trErrorTemp.frontAmp.value = "";
        // TR軸警告数(温度異常) - 後フィード
        result.trErrorTemp.rearAmp.value = "";
        break;
      case pressType.TS:
        // TS機種の場合、以下の値は対象外(空欄)とする
        // TR軸警告数(温度異常) - 前フィード リニア1
        result.trErrorTemp.frontLinear1.value = "";
        // TR軸警告数(温度異常) - 前フィード リニア2
        result.trErrorTemp.frontLinear2.value = "";
        // TR軸警告数(温度異常) - 後フィード リニア1
        result.trErrorTemp.rearLinear1.value = "";
        // TR軸警告数(温度異常) - 後フィード リニア2
        result.trErrorTemp.rearLinear2.value = "";
        break;
      default:
        break;
    }
  }
  return result;
};

// 差分結果算出処理(target:今回, source:前回, ref:比較基準日)
const calculateDiff = (pattern, target, source, ref) => {
  const isObject = (obj) =>
    obj && typeof obj === "object" && !Array.isArray(obj);
  const result = { ...target };

  // 今回の日付選択時のみ増分項目計算用に比較基準日のデータrefが必要
  // 前回、前々回の日付選択時はrefはnullとなっている
  if (
    pattern === "currentData"
      ? isObject(target) && isObject(source) && isObject(ref)
      : isObject(target) && isObject(source)
  ) {
    const reportEntry = Object.entries(reportData);

    reportEntry.forEach((entry) => {
      const categoryKey = entry[0];
      const categoryValue = entry[1];

      if (
        pattern === "currentData"
          ? hasOwnProperty(target, categoryKey) &&
            hasOwnProperty(source, categoryKey) &&
            hasOwnProperty(ref, categoryKey)
          : hasOwnProperty(target, categoryKey) &&
            hasOwnProperty(source, categoryKey)
      ) {
        if (isObject(categoryValue)) {
          // 前回との差計算
          if (
            hasOwnProperty(categoryValue, "difference") ||
            hasOwnProperty(categoryValue, "per")
          ) {
            if (
              hasOwnProperty(target[categoryKey], "total") &&
              hasOwnProperty(source[categoryKey], "total")
            ) {
              // 前回との差を出す項目は、前回のデータsourceと比較する
              const targetTotal = parseFloat(target[categoryKey].total.value);
              const sourceTotal = parseFloat(source[categoryKey].total.value);

              if (hasOwnProperty(categoryValue, "difference")) {
                const diff =
                  Math.round((targetTotal - sourceTotal) * 100) / 100;
                if (!Number.isNaN(diff)) {
                  Object.assign(result[categoryKey].difference, {
                    value: String(diff),
                  });
                } else {
                  Object.assign(result[categoryKey].difference, {
                    value: "",
                  });
                }
              }

              if (hasOwnProperty(categoryValue, "per")) {
                const per =
                  Math.round(
                    ((targetTotal - sourceTotal) / targetTotal) * 100 * 100
                  ) / 100;
                if (!Number.isNaN(per)) {
                  Object.assign(result[categoryKey].per, {
                    value: String(per),
                  });
                } else {
                  Object.assign(result[categoryKey].per, {
                    value: "",
                  });
                }
              }
            }
          }

          const categoryItem = Object.entries(categoryValue);

          categoryItem.forEach((item) => {
            const itemKey = item[0];
            const itemValue = item[1];

            // 増分計算
            if (hasOwnProperty(itemValue, "diff")) {
              if (
                pattern === "currentData"
                  ? hasOwnProperty(target[categoryKey], itemKey) &&
                    hasOwnProperty(ref[categoryKey], itemKey)
                  : hasOwnProperty(target[categoryKey], itemKey)
              ) {
                let itemDiff = NaN;
                // 今回の日付選択時、増分項目は比較基準日のデータrefと比較する
                if (pattern === "currentData") {
                  const targetValue = parseFloat(
                    target[categoryKey][itemKey].value
                  );
                  const refValue = parseFloat(ref[categoryKey][itemKey].value);

                  itemDiff = Math.round((targetValue - refValue) * 100) / 100;
                } else {
                  // 前回の日付、前々回の日付選択時はDBに登録済みの増分値をそのまま表示する
                  itemDiff = parseFloat(target[categoryKey][itemKey].diff);
                }
                if (!Number.isNaN(itemDiff)) {
                  Object.assign(result[categoryKey][itemKey], {
                    diff: String(itemDiff),
                  });
                } else {
                  Object.assign(result[categoryKey][itemKey], {
                    diff: "",
                  });
                }
              }
            }
          });
        }
      }
    });
  }
  return result;
};

// 自動判定要素情報取得処理
export const getCategoryInfo = (list, key, subKey) => {
  if (list) {
    for (let i = 0; i < list.length; i++) {
      const categoryKey = list[i].key;
      const categoryValue = list[i].item;

      if (key && categoryKey === key && categoryValue) {
        for (let j = 0; j < categoryValue.length; j++) {
          const itemKey = categoryValue[j].key;

          if (subKey && itemKey === subKey) {
            return categoryValue[j];
          }
        }
      }
    }
  }
  return null;
};

// 自動判定所見情報取得処理
const getOtherComment = (reportKey, itemKey, judgeString, value) => {
  const otherReportData = getOtherReportData(reportKey);
  let comment = "";

  if (otherReportData) {
    // 個別判定項目の場合
    if (judgeString === judgeList[6].value) {
      // B("B.")判定
      comment = otherReportData.B;
    } else if (judgeString === judgeList[7].value) {
      // C("C.")判定
      comment = otherReportData.C;
    } else {
      return comment;
    }

    if (reportKey === "pointLeak") {
      // ポイント漏れ状態(差圧)はデータ埋め込みが必要な文字列
      comment = comment.replace(
        "{{partsJpn}}",
        getOtherReportPartsName(itemKey, languageType.ja)
      );
      comment = comment.replace(
        "{{partsEng}}",
        getOtherReportPartsName(itemKey, languageType.en)
      );
    }
  } else if (hasOwnProperty(otherReportKindsList, reportKey)) {
    // 共通判定項目の場合はデータ埋め込みが必要な文字列
    const otherReportErrorsData = getOtherReportData("errors");

    if (judgeString === judgeList[6].value) {
      // B("B.")判定
      comment = otherReportErrorsData.B;
    } else if (judgeString === judgeList[7].value) {
      // C("C.")判定
      comment = otherReportErrorsData.C;
    } else {
      return comment;
    }

    if (otherReportErrorsData) {
      comment = comment.replace(
        "{{kindsJpn}}",
        getOtherReportKindName(reportKey, languageType.ja)
      );
      comment = comment.replace(
        "{{kindsEng}}",
        getOtherReportKindName(reportKey, languageType.en)
      );
      comment = comment.replace(
        "{{partsJpn}}",
        getOtherReportPartsName(itemKey, languageType.ja)
      );
      comment = comment.replace(
        "{{partsEng}}",
        getOtherReportPartsName(itemKey, languageType.en)
      );
      comment = comment.replaceAll("{{diff}}", createGroupNumberString(value));
    }
  }

  return comment;
};

// 自動判定所見欄コメント生成
const createOtherComment = (otherReport) => {
  // 所見欄自動入力
  let value = "";

  // ALL-A判定の場合
  if (otherReport.allA === true) {
    const allA = getOtherReportData("allA");
    otherReport.judgeData[String(1)] = { value: allA.value };
  }

  // index番号でソート
  const comments = Object.entries(otherReport.judgeData);
  comments.sort((a, b) => {
    if (Number(a[0]) < Number(b[0])) {
      return -1;
    }
    if (Number(a[0]) > Number(b[0])) {
      return 1;
    }
    return 0;
  });

  comments.forEach((comment) => {
    value = value === "" ? comment[1].value : `${value}\n${comment[1].value}`;
  });

  if (otherReport.existC === true) {
    value = `${value}\n${otherReportList.lastExistC.value}`;
  }

  return value;
};

// 自動判定結果算出処理
const calculateJudge = (
  currentData,
  type,
  refCurrentDateFormatJp,
  refCurrentDateFormatEn
) => {
  const result = { ...currentData };

  if (currentData && currentData.date && currentData.creationResult === false) {
    const reportEntry = Object.entries(currentData);
    const list = getReportCategory(type);
    const otherReport = { ...otherReportInfo };
    otherReport.judgeData = {};

    // HEAD文生成
    const month = formatTZ(toDate(new Date()), "MM", {
      timeZone: "Asia/Tokyo",
    });
    const day = formatTZ(toDate(new Date()), "dd", {
      timeZone: "Asia/Tokyo",
    });

    let head = "";
    if (type === pressType.TY) {
      // TY機種の場合は比較基準日無し
      head = getOtherReportData("head");
    } else {
      head = { ...otherReportList.headRef };
      head.value = head.value.replaceAll(
        "{{refCurrentDateJp}}",
        refCurrentDateFormatJp
      );
      head.value = head.value.replaceAll(
        "{{refCurrentDateEn}}",
        refCurrentDateFormatEn
      );
    }
    head.value = head.value.replaceAll("{{month}}", month);
    head.value = head.value.replaceAll("{{day}}", day);
    otherReport.judgeData[String(0)] = { value: head.value };

    // 大項目ループ
    reportEntry.forEach((entry) => {
      const reportKey = entry[0];
      const reportValue = entry[1];

      if (list && reportKey && reportValue) {
        const reportItem = Object.entries(reportValue);

        // 小項目ループ
        reportItem.forEach((item) => {
          const itemKey = item[0];
          const itemValue = item[1];

          if (itemKey && itemValue) {
            const categoryInfo = getCategoryInfo(list, reportKey, itemKey);

            // 自動判定対象項目の場合
            if (categoryInfo && categoryInfo.autoJudge === true) {
              // 計測値が空文字の項目は自動判定スキップ
              if (itemValue.value === "") {
                return;
              }

              // 増分値表示項目、かつ増分値が空文字の項目は自動判定スキップ
              if (hasOwnProperty(itemValue, "diff") && itemValue.diff === "") {
                return;
              }

              // 対象項目の閾値情報取得
              const judgeInfo = getJudgeInfo(type, reportKey, itemKey);

              if (judgeInfo) {
                const lowerLimitB = parseFloat(judgeInfo.lowerLimitB);
                const lowerLimitC = parseFloat(judgeInfo.lowerLimitC);

                const displayDiffValue =
                  hasOwnProperty(itemValue, "diff") && itemValue.diff !== ""
                    ? itemValue.diff
                    : itemValue.value;
                const targetValue = judgeInfo.judgeDiff
                  ? parseFloat(displayDiffValue)
                  : parseFloat(itemValue.value);

                let judgeString = judgeList[5].value;
                if (reportKey === "pointLeak") {
                  // ポイント漏れの場合は閾値がマイナス値であるため、比較条件が反転する
                  if (targetValue <= lowerLimitB && targetValue > lowerLimitC) {
                    judgeString = judgeList[6].value;
                    otherReport.allA = false;
                  } else if (targetValue <= lowerLimitC) {
                    judgeString = judgeList[7].value;
                    otherReport.allA = false;
                    otherReport.existC = true;
                  }
                } else {
                  // eslint-disable-next-line no-lonely-if
                  if (targetValue >= lowerLimitB && targetValue < lowerLimitC) {
                    judgeString = judgeList[6].value;
                    otherReport.allA = false;
                  } else if (targetValue >= lowerLimitC) {
                    judgeString = judgeList[7].value;
                    otherReport.allA = false;
                    otherReport.existC = true;
                  }
                }
                result[reportKey][itemKey].judge = judgeString;

                // 所見欄コメント文取得
                const comment = getOtherComment(
                  reportKey,
                  itemKey,
                  judgeString,
                  displayDiffValue
                );
                if (comment !== "") {
                  // コメントが存在する場合は登録
                  otherReport.judgeData[String(categoryInfo.index)] = {
                    value: comment,
                  };
                }
              }
            }
          }
        });
      }
    });

    // 自動所見コメントの生成
    const value = createOtherComment(otherReport);

    // 自動所見の設定
    if (value !== "") {
      result.other = value;
    }
  }

  return result;
};

// 取得データから行に対応する情報を取得(値)
const serachValue = (data, category, item, language) => {
  if (data && category && category.key) {
    const items = data[category.key];

    if (item.key && items && items[item.key]) {
      if (items[item.key].diff) {
        return `${item.beforeUnit[language]}${items[item.key].diff}${
          item.unit[language]
        }`;
      }
      return `${item.beforeUnit[language]}${items[item.key].value}${
        item.unit[language]
      }`;
    }
  }
  return item.unit[language];
};

// 取得データから行に対応する情報を取得(判定)
const serachJudge = (data, category, item) => {
  if (data && category) {
    const items = data[category];

    if (items && items[item]) {
      return items[item].judge;
    }
  }
  return "";
};

// 判定結果の文字色判定
const changeJudgeColor = (data, category, item) => {
  let color = "black";

  if (data && data[category] && data[category][item]) {
    switch (data[category][item].judge) {
      case "A":
      case "A.":
        color = reportTableColor.levelA;
        break;
      case "B":
      case "B.":
        color = reportTableColor.levelB;
        break;
      case "C":
      case "C.":
        color = reportTableColor.levelC;
        break;
      default:
        color = "black";
        break;
    }
  }
  return color;
};

// 日付データ取得
const getTargetDate = (refDate, beforeDay) => {
  const dateObj = new Date(refDate);
  dateObj.setDate(dateObj.getDate() - beforeDay);
  // 文字列に変換
  const targetDate = formatTZ(toDate(dateObj), "yyyy-MM-dd", {
    timeZone: "Asia/Tokyo",
  });
  return targetDate;
};

// 稼働日判定
const judgeOperationDay = (targetRes) => {
  // ダイハイト調整距離
  const dieHeightRange =
    targetRes.data.diagnosticChartDataDaily.dieHeightRange.total.value;
  // クラッチ動作時間
  const clutchTime =
    targetRes.data.diagnosticChartDataDaily.clutchTime.total.value;
  if (dieHeightRange > 0 && clutchTime > 0) {
    return true;
  }
  return false;
};

// 所見欄比較日フォーマット生成
const createRefDateFormat = (dateString) => {
  const date = new Date(dateString);
  // 年の下2桁を取得
  const year = String(date.getFullYear()).slice(-2);
  // 月を2桁にフォーマット
  const month = String(date.getMonth() + 1).padStart(2, "0");
  // 日を2桁にフォーマット
  const day = String(date.getDate()).padStart(2, "0");

  const formattedDateJp = `${year}年${month}月${day}日`;
  const formattedDateEn = `${year}/${month}/${day}`;

  return { formattedDateJp, formattedDateEn };
};

/**
 * Slice.
 *
 * @type {object}
 */
export const slice = createSlice({
  // Slice名
  name: storeKeyName.report,

  // 初期State
  initialState: {
    // 読み込み中フラグ（true: 表示、false: 非表示）
    isLoading: false,
    // PDF生成中フラグ（true: 表示、false: 非表示）
    isCreating: false,
    // PDF出力言語
    language: null,
    // エラー内容
    error: null,
    // 判定結果入力済み日付
    judgedDates: null,
    // 診断カルテデータ(今回)
    currentData: resetData(),
    // 診断カルテデータ(前回)
    beforeData1: resetData(),
    // 診断カルテデータ(前々回)
    beforeData2: resetData(),
    // 診断カルテデータ(今回)の比較日データ
    refCurrentData: resetData(),
    // 所見欄比較日フォーマット(今回,日本語)
    refCurrentDateFormatJp: "--年--月--日",
    // 所見欄比較日フォーマット(今回,英語)
    refCurrentDateFormatEn: "--/--/--",
    // fetchGetReportDataが終了したときにisLoadingをfalseにするために使用
    // 非同期実行中フラグ(今回)
    currentAsyncFlag: false,
  },

  // Reducer
  reducers: {
    // 非同期関数実行開始
    fetchAsyncStart(state) {
      // isLoadingがfalseだった場合は、ここで再度Trueにする
      if (!state.isLoading) {
        state.isLoading = true;
      }
      state.currentAsyncFlag = true;
    },
    // 非同期関数実行終了
    fetchAsyncEnd(state) {
      state.currentAsyncFlag = false;
    },
    // 通信開始
    fetchStart(state) {
      state.isLoading = true;
    },
    // 通信失敗
    fetchFailure(state, action) {
      state.error = action.payload;
      state.isLoading = false;
    },
    // 通信成功
    fetchSuccess(state) {
      state.error = null;
      state.isLoading = false;
    },
    // 通信成功(fetchGetReportDataからの呼出し専用)
    fetchSuccessJudge(state) {
      state.error = null;
      // fetchGetReportDataからの呼出しは、非同期処理が終了した場合のみisLoadingをfalseにする
      if (!state.currentAsyncFlag) {
        state.isLoading = false;
      }
    },
    // PDF生成開始
    fetchCreateStart(state, action) {
      state.language = action.payload;
      state.isCreating = true;
    },
    // PDF生成失敗
    fetchCreateFailure(state, action) {
      state.error = action.payload;
      state.language = null;
      state.isCreating = false;
    },
    // PDF生成成功
    fetchCreateSuccess(state) {
      state.error = null;
      state.language = null;
      state.isCreating = false;
    },
    // クリア
    fetchClear(state) {
      state.error = null;
      state.language = null;
      state.judgedDates = null;
      state.currentData = resetData();
      state.beforeData1 = resetData();
      state.beforeData2 = resetData();
      state.refCurrentData = resetData();
      state.refCurrentDateFormatJp = "--年--月--日";
      state.refCurrentDateFormatEn = "--/--/--";
      state.isLoading = false;
      state.isCreating = false;
      state.currentAsyncFlag = false;
    },
    // 診断カルテデータクリア
    fetchClearData(state, action) {
      switch (action.payload) {
        case targetData.current:
          state.currentData = resetData();
          state.refCurrentData = resetData();
          state.refCurrentDateFormatJp = "--年--月--日";
          state.refCurrentDateFormatEn = "--/--/--";
          break;
        case targetData.before1:
          state.beforeData1 = resetData();
          break;
        case targetData.before2:
          state.beforeData2 = resetData();
          break;
        default:
          break;
      }
    },
    // 診断カルテデータ更新
    fetchUpdateData(state, action) {
      switch (action.payload.target) {
        case targetData.current:
          state.currentData = mergeData(reportData, action.payload.data);
          state.currentData = unusedData(
            state.currentData,
            action.payload.pressType
          );
          break;
        case targetData.before1:
          state.beforeData1 = mergeData(reportData, action.payload.data);
          state.beforeData1 = unusedData(
            state.beforeData1,
            action.payload.pressType
          );
          break;
        case targetData.before2:
          state.beforeData2 = mergeData(reportData, action.payload.data);
          state.beforeData2 = unusedData(
            state.beforeData2,
            action.payload.pressType
          );
          break;
        case targetData.refCurrent:
          state.refCurrentData = mergeData(reportData, action.payload.data);
          state.refCurrentData = unusedData(
            state.refCurrentData,
            action.payload.pressType
          );
          break;
        default:
          break;
      }
    },
    // 差分データの算出
    fetchCalculateData(state) {
      state.currentData = calculateDiff(
        "currentData",
        state.currentData,
        state.beforeData1,
        state.refCurrentData
      );
      state.beforeData1 = calculateDiff(
        "beforeData1",
        state.beforeData1,
        state.beforeData2,
        null
      );
      state.beforeData2 = calculateDiff(
        "beforeData2",
        state.beforeData2,
        null,
        null
      );
    },
    // 自動判定処理実行
    fetchCalculateJudge(state, action) {
      state.currentData = calculateJudge(
        state.currentData,
        action.payload.pressType,
        state.refCurrentDateFormatJp,
        state.refCurrentDateFormatEn
      );
    },
    // 判定結果入力済み日付リストの取得
    fetchUpdateJudgedDates(state, action) {
      state.judgedDates = null;
      state.judgedDates = action.payload;
    },
    // 診断カルテPDF作成
    fetchCreateReportPDF(state, action) {
      // 全体のページ設定
      const pdf = new JsPDF({
        operation: "portrait",
        unit: "mm",
        format: "a3",
      });

      // 日本語フォントをbase64変換して使用可能にする
      pdf.addFileToVFS("ipaexg.ttf", ipaexg);
      pdf.addFont("ipaexg.ttf", "ipaexg", "normal");
      pdf.setFont("ipaexg", "normal");

      // ページ余白設定(mm)
      const margin = {
        top: 9,
        bottom: 4,
        left: 20,
        right: 1,
      };

      // コンテンツ生成
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const defaultFontSize = pdf.getFontSize();
      const largeFontSize = 16;
      const smallFontSize = 12;

      // // ロゴサイズ
      const logoWidth = 41;
      const logoHeight = 16;
      const headerHeight = 36;

      // 診断カルテ情報作成
      const headContents = [];
      const headContent = [];
      reportLabel.forEach((item) => {
        let text = item.label[state.language];
        if (item.key === "currentData" && state.currentData.date) {
          text = text.replace(
            "YYYY/MM/DD",
            formatTZ(toDate(new Date(state.currentData.date)), "yyyy/MM/dd", {
              timeZone: "Asia/Tokyo",
            })
          );
        } else if (item.key === "beforeData1" && state.beforeData1.date) {
          text = text.replace(
            "YYYY/MM/DD",
            formatTZ(toDate(new Date(state.beforeData1.date)), "yyyy/MM/dd", {
              timeZone: "Asia/Tokyo",
            })
          );
        } else if (item.key === "beforeData2" && state.beforeData2.date) {
          text = text.replace(
            "YYYY/MM/DD",
            formatTZ(toDate(new Date(state.beforeData2.date)), "yyyy/MM/dd", {
              timeZone: "Asia/Tokyo",
            })
          );
        }
        headContent.push({
          rowSpan: item.rowSpan,
          colSpan: item.colSpan,
          content: text,
          styles: {
            fillColor: item.color,
            halign: "left",
          },
        });
      });
      headContents.push(headContent);

      const bodyContents = [];
      const createCategoryRow = (category, parentIndex) => {
        category.item.forEach((item, index) => {
          const bodyContent = [];

          if (index === 0) {
            bodyContent.push({
              rowSpan: category.rowSpan,
              colSpan: category.colSpan,
              content: String(parentIndex + 1),
            });
            bodyContent.push({
              rowSpan: category.rowSpan,
              colSpan: category.colSpan,
              content: category.label[state.language],
            });
          }
          bodyContent.push({
            content:
              category.key === "trErrorPosi" &&
              item.key === "addAxis" &&
              state.language === languageType.en
                ? `${item.label[state.language]}\n\n`
                : item.label[state.language],
          });
          bodyContent.push({
            content: serachValue(
              state.currentData,
              category,
              item,
              state.language
            ),
            styles: {
              halign: "right",
            },
          });
          bodyContent.push({
            content: serachJudge(state.currentData, category.key, item.key),
            styles: {
              halign: "center",
              textColor: changeJudgeColor(
                state.currentData,
                category.key,
                item.key
              ),
            },
          });
          bodyContent.push({
            content: serachValue(
              state.beforeData1,
              category,
              item,
              state.language
            ),
            styles: {
              halign: "right",
            },
          });
          bodyContent.push({
            content: serachJudge(state.beforeData1, category.key, item.key),
            styles: {
              halign: "center",
              textColor: changeJudgeColor(
                state.beforeData1,
                category.key,
                item.key
              ),
            },
          });
          bodyContent.push({
            content: serachValue(
              state.beforeData2,
              category,
              item,
              state.language
            ),
            styles: {
              halign: "right",
            },
          });
          bodyContent.push({
            content: serachJudge(state.beforeData2, category.key, item.key),
            styles: {
              halign: "center",
              textColor: changeJudgeColor(
                state.beforeData2,
                category.key,
                item.key
              ),
            },
          });
          if (index === 0) {
            bodyContent.push({
              rowSpan: category.rowSpan,
              colSpan: category.colSpan,
              content: state.currentData[category.key]
                ? state.currentData[category.key].note
                : "",
            });
            bodyContent.push({
              rowSpan: category.rowSpan,
              colSpan: category.colSpan,
              content: category.explanation[state.language],
            });
          }
          bodyContents.push(bodyContent);
        });
      };

      action.payload.categoryList.forEach((category, index) => {
        createCategoryRow(category, index);
      });

      // 備考欄に埋め込むサブ表のヘッダー情報
      const subHeadContents = [
        [
          {
            rowSpan: 2,
            colSpan: 2,
            content: "",
            styles: { fillColor: reportTableColor.defalut },
          },
          {
            rowSpan: 1,
            colSpan: 2,
            content: trReferenceTableLabel.motorType[state.language],
            styles: { fillColor: reportTableColor.defalut },
          },
        ],
        [
          {
            rowSpan: 1,
            colSpan: 1,
            content: trReferenceTableLabel.linear[state.language],
            styles: { fillColor: reportTableColor.defalut },
          },
          {
            rowSpan: 1,
            colSpan: 1,
            content: trReferenceTableLabel.rotary[state.language],
            styles: { fillColor: reportTableColor.defalut },
          },
        ],
      ];

      // 備考欄に埋め込むサブ表のボディ情報
      const subBodyContents = (type) => {
        const contents = [
          [
            {
              rowSpan: 3,
              colSpan: 1,
              content: trReferenceTableLabel.judge[state.language],
              styles: { fillColor: reportTableColor.defalut },
            },
            {
              rowSpan: 1,
              colSpan: 1,
              content: trReferenceTableLabel.A[state.language],
              styles: { fillColor: reportTableColor.defalut },
            },
            {
              rowSpan: 1,
              colSpan: 1,
              content: type === "temp" ? "～ 1" : "0 ～ 2",
              styles: { fillColor: reportTableColor.defalut },
            },
            {
              rowSpan: 1,
              colSpan: 1,
              content: "0",
              styles: { fillColor: reportTableColor.defalut },
            },
          ],
          [
            {
              rowSpan: 1,
              colSpan: 1,
              content: trReferenceTableLabel.B[state.language],
              styles: { fillColor: reportTableColor.defalut },
            },
            {
              rowSpan: 1,
              colSpan: 1,
              content: type === "temp" ? "2 ～" : "3 ～",
              styles: { fillColor: reportTableColor.defalut },
            },
            {
              rowSpan: 1,
              colSpan: 1,
              content: "1 ～",
              styles: { fillColor: reportTableColor.defalut },
            },
          ],
          [
            {
              rowSpan: 1,
              colSpan: 1,
              content: trReferenceTableLabel.C[state.language],
              styles: { fillColor: reportTableColor.defalut },
            },
            {
              rowSpan: 1,
              colSpan: 1,
              content: "5 ～",
              styles: { fillColor: reportTableColor.defalut },
            },
            {
              rowSpan: 1,
              colSpan: 1,
              content: "3 ～",
              styles: { fillColor: reportTableColor.defalut },
            },
          ],
        ];

        return contents;
      };

      // 診断カルテ(表)生成
      pdf.autoTable({
        styles: {
          font: "ipaexg",
          fontStyle: "normal",
          valign: "top",
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          fontSize: state.language === languageType.ja ? 8 : 7,
          textColor: [0, 0, 0],
          minCellHeight: 7,
        },
        columnStyles: {
          0: {
            cellWidth: 7,
            fillColor: reportTableColor.currentData,
            halign: "left",
          },
          1: {
            cellWidth: 32,
            fillColor: reportTableColor.currentData,
            halign: "left",
          },
          2: {
            cellWidth: 32,
            fillColor: reportTableColor.currentData,
            halign: "left",
          },
          3: {
            cellWidth: 27,
            fillColor: reportTableColor.currentData,
            halign: "left",
          },
          4: {
            cellWidth: 11,
            fillColor: reportTableColor.currentData,
            halign: "left",
          },
          5: {
            cellWidth: 27,
            fillColor: reportTableColor.beforeData1,
            halign: "left",
          },
          6: {
            cellWidth: 11,
            fillColor: reportTableColor.beforeData1,
            halign: "left",
          },
          7: {
            cellWidth: 27,
            fillColor: reportTableColor.beforeData2,
            halign: "left",
          },
          8: {
            cellWidth: 11,
            fillColor: reportTableColor.beforeData2,
            halign: "left",
          },
          9: {
            cellWidth: 40,
            fillColor: reportTableColor.default,
            halign: "left",
          },
          10: {
            fillColor: reportTableColor.default,
            halign: "left",
          },
        },
        margin: {
          top: margin.top + headerHeight + 2,
          left: margin.left,
          right: margin.right,
          bottom: margin.bottom + smallFontSize,
        },
        startY: margin.top + headerHeight + 2,
        head: headContents,
        body: bodyContents,
        didDrawCell: (data) => {
          if (
            (action.payload.pressType === pressType.TR ||
              action.payload.pressType === pressType.TS) &&
            data.cell.section === "body" &&
            data.column.index === 10 &&
            (data.row.index === 19 ||
              data.row.index === 30 ||
              data.row.index === 37)
          ) {
            pdf.autoTable({
              startY:
                data.row.index === 37 ? data.cell.y + 18 : data.cell.y + 14,
              margin: { left: data.cell.x + 1 },
              tableWidth: data.cell.width - 2,
              styles: {
                font: "ipaexg",
                fontStyle: "normal",
                valign: "middle",
                halign: "center",
                lineWidth: 0.5,
                lineColor: [0, 0, 0],
                fontSize: 6,
                textColor: [0, 0, 0],
                fillColor: reportTableColor.defalut,
              },
              head: subHeadContents,
              body:
                data.row.index === 19
                  ? subBodyContents("temp")
                  : subBodyContents("posi"),
            });
          }
        },
      });

      // 所見ページ作成
      pdf.addPage();

      // 診断カルテ(所見欄)生成
      pdf.autoTable({
        styles: {
          font: "ipaexg",
          fontStyle: "normal",
          valign: "top",
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
          fontSize: 12.5,
          textColor: [0, 0, 0],
        },
        margin: {
          top: margin.top + headerHeight + 2,
          left: margin.left,
          right: margin.right,
          bottom: margin.bottom + smallFontSize,
        },
        startY: margin.top + headerHeight + 2,
        head: [
          [
            {
              content: reportOtherLabel[state.language].replace("\n", " "),
              styles: {
                fillColor: reportTableColor.default,
                halign: "center",
                fontSize: smallFontSize,
              },
            },
          ],
        ],
        body: [
          [
            {
              content: state.currentData.other || "",
              styles: {
                fillColor: reportTableColor.default,
                halign: "left",
                minCellHeight:
                  pageHeight -
                  (margin.top -
                    1 +
                    headerHeight +
                    margin.bottom +
                    smallFontSize * 2),
              },
            },
          ],
        ],
      });

      // 合計ページ数取得
      const totalPage = pdf.internal.getNumberOfPages();
      for (let i = 0; i < totalPage; i++) {
        // 追加編集するページ番号を指定
        pdf.setPage(i);

        // ヘッダー情報生成(見出し、拠点情報等)
        pdf.setFontSize(defaultFontSize);
        pdf.text(reportFileLabel[state.language], margin.left, margin.top, {
          baseline: "top",
        });

        pdf.setFontSize(smallFontSize);

        // 拠点情報
        const companyLabel = reportFormLabel.companyName[state.language];
        pdf.text(
          `${companyLabel} : ${state.currentData.companyName || ""}`,
          margin.left + ((pageWidth - margin.left) * 1) / 100,
          margin.top + defaultFontSize / 2 + 2,
          { baseline: "top" }
        );

        const workNumberLabel = reportFormLabel.workNumber[state.language];
        pdf.text(
          `${workNumberLabel} : ${state.currentData.workNumber || ""}`,
          margin.left + ((pageWidth - margin.left) * 1) / 100,
          margin.top + defaultFontSize / 2 + smallFontSize / 2 + 3,
          { baseline: "top" }
        );
        const modelLabel = reportFormLabel.model[state.language];
        pdf.text(
          `${modelLabel} : ${state.currentData.model || ""}`,
          margin.left + ((pageWidth - margin.left) * 22) / 100,
          margin.top + defaultFontSize / 2 + smallFontSize / 2 + 3,
          { baseline: "top" }
        );
        const dueDateLabel = reportFormLabel.dueDate[state.language];
        pdf.text(
          `${dueDateLabel} : ${
            state.currentData.dueDate
              ? formatTZ(
                  toDate(new Date(state.currentData.dueDate)),
                  state.language === languageType.ja ? "yyyy年MM月" : "yyyy/MM",
                  { timeZone: "Asia/Tokyo" }
                )
              : ""
          }`,
          margin.left + ((pageWidth - margin.left) * 1) / 100,
          margin.top +
            defaultFontSize / 2 +
            smallFontSize / 2 +
            smallFontSize / 2 +
            4,
          { baseline: "top" }
        );
        // 【"使用年数"&"機番" 非表示対応】graphqlはそのまま、画面表示からは対象外とする
        // const useYearsLabel = reportFormLabel.useYears[state.language];
        // pdf.text(
        //   `${useYearsLabel} : ${
        //     state.currentData.useYears
        //       ? `${parseFloat(state.currentData.useYears).toFixed(1)}${
        //           state.language === languageType.ja ? "年" : "years"
        //         }`
        //       : ""
        //   }`,
        //   margin.left + ((pageWidth - margin.left) * 22) / 100,
        //   margin.top +
        //     defaultFontSize / 2 +
        //     smallFontSize / 2 +
        //     smallFontSize / 2 +
        //     4,
        //   { baseline: "top" }
        // );
        // const machineNumberLabel = reportFormLabel.modelNumber[state.language];
        // pdf.text(
        //   `${machineNumberLabel} : ${state.currentData.modelNumber}`,
        //   margin.left + ((pageWidth - margin.left) * 43) / 100,
        //   margin.top +
        //     defaultFontSize / 2 +
        //     smallFontSize / 2 +
        //     smallFontSize / 2 +
        //     4,
        //   { baseline: "top" }
        // );

        // ロゴ
        pdf.addImage(
          corporate,
          "JPEG",
          pageWidth - margin.right - logoWidth - 2,
          margin.top,
          logoWidth,
          logoHeight
        );

        // 作成日
        pdf.setFontSize(smallFontSize);
        const createdDayDefault =
          state.language === languageType.ja ? "YYYY年MM月DD日" : "YYYY/MM/DD";
        const createdDay = state.currentData.createdDay
          ? formatTZ(
              toDate(new Date(state.currentData.createdDay)),
              state.language === languageType.ja
                ? "yyyy年MM月dd日"
                : "yyyy/MM/dd",
              { timeZone: "Asia/Tokyo" }
            )
          : createdDayDefault;
        pdf.text(
          createdDay,
          margin.left + ((pageWidth - margin.left) * 40) / 100,
          margin.top,
          { baseline: "top" }
        );

        // 判定基準
        pdf.setFontSize(largeFontSize);
        pdf.setTextColor(reportTableColor.levelA);
        pdf.text(
          judgeLabel.A[state.language],
          margin.left + ((pageWidth - margin.left) * 60) / 100,
          margin.top,
          { baseline: "top" }
        );
        pdf.setTextColor(reportTableColor.levelB);
        pdf.text(
          judgeLabel.B[state.language],
          margin.left + ((pageWidth - margin.left) * 60) / 100,
          margin.top + largeFontSize / 2 + 1,
          { baseline: "top" }
        );
        pdf.setTextColor(reportTableColor.levelC);
        pdf.text(
          judgeLabel.C[state.language],
          margin.left + ((pageWidth - margin.left) * 60) / 100,
          margin.top + largeFontSize / 2 + largeFontSize / 2 + 2,
          { baseline: "top" }
        );

        // フッター情報生成(ページ番号 of ページ数)
        pdf.setFontSize(smallFontSize);
        pdf.setTextColor("black");

        pdf.text(
          `Page ${
            pdf.internal.getCurrentPageInfo().pageNumber
          } of ${totalPage}`,
          pageWidth / 2,
          pageHeight - smallFontSize / 2 - margin.bottom,
          { baseline: "top", align: "center" }
        );
      }

      // PDF出力(押下した出力ボタンに応じてファイル名に言語サフィックスを付与する)
      pdf.save(
        `${action.payload.fileName}_${
          state.language === languageType.ja ? "jpn" : "eng"
        }.pdf`
      );
    },
    fetchSetRefDateFormat(state, action) {
      // 所見欄の比較日用の日付フォーマット設定
      const formattedDateInfo = createRefDateFormat(action.payload.date);
      state.refCurrentDateFormatJp = formattedDateInfo.formattedDateJp;
      state.refCurrentDateFormatEn = formattedDateInfo.formattedDateEn;
    },
  },
});

/**
 * Actions.
 *
 * @type {object}
 */
export const {
  fetchStart,
  fetchFailure,
  fetchSuccess,
  fetchSuccessJudge,
  fetchCreateStart,
  fetchCreateFailure,
  fetchCreateSuccess,
  fetchClear,
  fetchCreateReportPDF,
  fetchClearData,
  fetchUpdateData,
  fetchCalculateData,
  fetchCalculateJudge,
  fetchUpdateJudgedDates,
  fetchSetRefDateFormat,
  fetchAsyncStart,
  fetchAsyncEnd,
} = slice.actions;

/**
 * Selectors.
 *
 * @type {function}
 */
export const selector = (stateAll) =>
  // すべてのStoreのStateが含まれているため、特定のStoreのStateのみ返す
  stateAll[storeKeyName.report];

/**
 * fetchCreatePDF.
 *
 */
export const fetchCreatePDF = (payload) => async (dispatch) => {
  // PDFデータ生成
  dispatch(fetchCreateReportPDF(payload));
  // 通信終了
  dispatch(fetchCreateSuccess());
};

/**
 * fetchGetJudgedDates.
 *
 */
export const fetchGetJudgedDates = (office) => async (dispatch) => {
  // 通信開始
  dispatch(fetchStart());

  // 診断カルテデータ取得APIの実行
  const { data, error } = await executeListJudgedDates(office).catch(
    handleError
  );
  if (error) {
    dispatch(fetchFailure(error));
    return;
  }

  // データ更新
  dispatch(fetchUpdateJudgedDates(data.judgedDates));

  // 通信終了
  dispatch(fetchSuccess());
};

/**
 * fetchResetReportData.
 *
 */
export const fetchResetReportData = (target) => async (dispatch) => {
  // 通信開始
  dispatch(fetchStart());

  // データ更新
  dispatch(fetchClearData(target));
  dispatch(fetchCalculateData());

  // 通信終了
  dispatch(fetchSuccess());
};

/**
 * fetchUpdateRefData.
 *
 */
const fetchUpdateRefData = (targetRes, beforeDay, target, type) => async (
  dispatch
) => {
  dispatch(
    fetchSetRefDateFormat({
      target,
      date: targetRes.data.diagnosticChartDataDaily.date,
      beforeDay,
    })
  );
  dispatch(
    fetchUpdateData({
      target: targetData.refCurrent,
      data: targetRes.data.diagnosticChartDataDaily,
      pressType: type,
    })
  );
};

/**
 * fetchGetReportData.
 *
 */
export const fetchGetReportData = (office, target, date, type) => async (
  dispatch
) => {
  // 処理方針
  // 1. 選択した日付の診断カルテデータを取得する
  // 2. storeに保存する
  // 3. 選択した日付から120日前～150日前の診断カルテデータを取得する
  // 4. 稼働日判定を行い、稼働日であればstoreに保存する
  // 5. 稼働日が見つからなかった場合、119日前～0日前の診断カルテデータを取得する
  // 6. 稼働日判定を行い、稼働日であればstoreに保存する

  // 通信開始
  dispatch(fetchStart());

  // 診断カルテデータクリア
  dispatch(fetchClearData(target));

  // 診断カルテデータ取得APIの実行
  let response;
  switch (target) {
    // 選択した日付のデータを取得する
    case targetData.current:
      response = await executeGetDiagnosticChartDataDaily(office, date).catch(
        handleError
      );
      break;
    case targetData.before1:
    case targetData.before2:
      // 前回、前々回データは選択した日付から一番近いカルテ登録済みデータを取得する
      response = await executeGetReferenceDiagnosticChartDataDaily(
        office,
        date
      ).catch(handleError);
      break;
    default:
      break;
  }

  if (response.error) {
    dispatch(fetchFailure(response.error));
    return;
  }

  // データ更新
  dispatch(
    fetchUpdateData({
      target,
      data: response.data.diagnosticChartDataDaily,
      pressType: type,
    })
  );

  if (
    !response.data.diagnosticChartDataDaily ||
    !response.data.diagnosticChartDataDaily.date
  ) {
    dispatch(fetchFailure(response.error));
    return;
  }

  // 機種タイプTYは増分項目がないため、比較基準日検索処理をスキップ
  // 前回日付、前々回日付のデータは比較基準日を取得する必要がないため、比較基準日を検索処理をスキップ
  if (type !== pressType.TY && target === targetData.current) {
    // 選択した日付(前回、前々回の場合は実際に取得したカルテ登録済みデータの日付になる)
    const refDate = response.data.diagnosticChartDataDaily.date;

    // 稼働日判定フラグ
    let isOperatingDay = false;

    // 比較基準日の検索処理開始
    dispatch(fetchAsyncStart());

    // 比較基準日の検索
    // 検索条件1：Web画面から指定された日付の120日前～150日前の新しい日付順
    for (let beforeDay = 120; beforeDay <= 150; beforeDay++) {
      // 日付データ生成
      const targetDate = getTargetDate(refDate, beforeDay);

      // eslint-disable-next-line no-await-in-loop
      const targetRes = await executeGetDiagnosticChartDataDaily(
        office,
        targetDate
      ).catch(handleError);

      if (targetRes.error) {
        dispatch(fetchFailure(targetRes.error));
        return;
      }

      // targetDateの診断カルテデータが無い場合もあり得るので、空なら次のtargetDateへ
      if (Object.keys(targetRes.data.diagnosticChartDataDaily).length === 0) {
        // eslint-disable-next-line no-continue
        continue;
      }

      // 稼働日判定
      isOperatingDay = judgeOperationDay(targetRes);

      // 稼働日であればstoreに保存する
      if (isOperatingDay) {
        // eslint-disable-next-line no-await-in-loop
        dispatch(fetchUpdateRefData(targetRes, beforeDay, target, type));
        break;
      }
    }

    // 稼働日が見つからなかった場合
    if (!isOperatingDay) {
      // 検索条件2：Web画面から指定された日付の119日前～Web画面から指定された日付の古い日付順
      for (let beforeDay = 119; beforeDay >= 0; beforeDay--) {
        // 日付データ生成
        const targetDate = getTargetDate(refDate, beforeDay);

        // eslint-disable-next-line no-await-in-loop
        const targetRes = await executeGetDiagnosticChartDataDaily(
          office,
          targetDate
        ).catch(handleError);

        if (targetRes.error) {
          dispatch(fetchFailure(targetRes.error));
          return;
        }

        // targetDateの診断カルテデータが無い場合もあり得るので、空なら次のtargetDateへ
        if (Object.keys(targetRes.data.diagnosticChartDataDaily).length === 0) {
          // eslint-disable-next-line no-continue
          continue;
        }

        // 稼働日判定
        isOperatingDay = judgeOperationDay(targetRes);

        // 稼働日であればstoreに保存する
        if (isOperatingDay) {
          dispatch(fetchUpdateRefData(targetRes, beforeDay, target, type));
          break;
        }
      }
    }

    // 比較基準日の検索処理終了
    dispatch(fetchAsyncEnd());
  }

  // 差分計算
  dispatch(fetchCalculateData());
  // 自動判定
  dispatch(fetchCalculateJudge({ pressType: type }));

  // 通信終了
  dispatch(fetchSuccessJudge());
};

/**
 * fetchSendReportData.
 *
 */
export const fetchSendReportData = (payload) => async (dispatch) => {
  // 通信開始
  dispatch(fetchStart());

  // 診断カルテデータ取得APIの実行
  const { data, error } = await executeUpdateDiagnosticChartDataDaily(
    payload
  ).catch(handleError);
  if (error) {
    dispatch(fetchFailure(error));
    return;
  }

  // データ更新
  dispatch(
    fetchUpdateData({
      target: targetData.current,
      data: data.diagnosticChartDataDaily,
      pressType: payload.pressType,
    })
  );
  dispatch(fetchCalculateData());
  dispatch(fetchGetJudgedDates(data.diagnosticChartDataDaily.office));

  // 通信終了
  dispatch(fetchSuccess());
};
